import { AnimationProps, IconSet, ServiceLayer } from '@Map/services/types';
import { CompositeLayerProps, Filter } from '@Map/layers/types';
import type VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import type MapView from '@arcgis/core/views/MapView';
import { findOwnerVtl } from '@Map/esri/utils/rendererHelpers';

import Events from '@Map/events/Events';
import HighlightBackgroundLayer from './creators/HighlightBackgroundLayer';
import HighlightForegroundLayer from './creators/HighlightForegroundLayer';
import { LAYER_UPDATED } from '@Map/layers/constants';
// import LineSymboCreatorlLayer from '../creators/LineSymbolCreatorLayer';
import MainLayer from './creators/MainLayer';
import SelectedLayer from './creators/SelectedLayer';
import logger from '@Map/logger/Logger';
import GeoJSONLayerView from '@arcgis/core/views/layers/GeoJSONLayerView';
import { ResourceHandle } from '@arcgis/core/core/Handles';

type LayerCreators = MainLayer | SelectedLayer;
// | HighlightBackgroundLayer
// | HighlightForegroundLayer
// | ClusterCreatorLayer
// | LineSymboCreatorlLayer
// | FlowCreatorLayer;

export default class CompositeLayer extends Events {
	private _vectorTileLayer?: VectorTileLayer;
	private _view?: MapView;
	private _baseStyleSnapshot: Map<string, ServiceLayer> = new Map();
	private _geoJsonLayerView?: GeoJSONLayerView;
	private _geoJsonHighlightHandle?: ResourceHandle;
	private _layer!: MainLayer;
	private _selectedLayer: SelectedLayer | undefined = undefined;
	private _highlightBackgroundLayer!: HighlightBackgroundLayer;
	private _highlightForegroundLayer!: HighlightForegroundLayer;
	// private _lineSymbolLayer: LineSymboCreatorlLayer | undefined = undefined;
	private _composite!: LayerCreators[];
	private _layerInfo: CompositeLayerProps;
	private _iconSet: IconSet = {};
	private _selectedItems: string[] = [];
	private _highlightItems: string[] = [];
	private _filter: Filter | undefined = undefined;
	private _assetFilter: boolean | string[] = false;
	private _invalid = false;

	constructor(
		layerInfo: CompositeLayerProps,
		vectorTileLayer?: VectorTileLayer,
	) {
		super();
		this._vectorTileLayer = vectorTileLayer;
		this._layerInfo = { ...layerInfo };

		if (!this._isValidLayer(layerInfo)) {
			this._invalid = true;
			return;
		}

		this._createAllLayers(layerInfo);

		this.draw();
	}
	set vectorTileLayer(value: VectorTileLayer) {
		this._vectorTileLayer = value;
		this._composite.forEach((layer: LayerCreators) => {
			layer.vectorTileLayer = value;
		});
	}

	set view(value: MapView | undefined) {
		this._view = value;
	}

	/**
	 * Resolves the VectorTileLayer that owns the given style-layer id.
	 * Tries the cached `_vectorTileLayer` first; falls back to iterating
	 * `view.map.layers` via `findOwnerVtl`. On a successful fallback the
	 * cache is updated so subsequent calls hit the fast path. Captures a
	 * snapshot of the resolved style layer on first hit (lazy, idempotent).
	 * Returns null when no VTL on the map owns the id.
	 */
	findVectorTileLayer(styleLayerId: string): VectorTileLayer | null {
		// Guard `getStyleLayer` with `.loaded` — Esri's VTL throws
		// "Cannot read properties of undefined" when the style spec hasn't
		// loaded yet (e.g. during initial layer wiring or before a tile
		// service has resolved). The existing `findOwnerVtl` helper uses
		// the same guard.
		const cached = this._vectorTileLayer;
		const cachedHit =
			cached && cached.loaded
				? cached.getStyleLayer(styleLayerId)
				: undefined;
		if (cachedHit && cached) {
			this._captureBaseSnapshotIfNeeded(styleLayerId, cachedHit);
			return cached;
		}
		if (!this._view) return null;
		const owner = findOwnerVtl(this._view, styleLayerId) as
			| VectorTileLayer
			| null;
		if (!owner) return null;
		this._vectorTileLayer = owner;
		const styleLayer = owner.loaded
			? owner.getStyleLayer(styleLayerId)
			: undefined;
		if (styleLayer) {
			this._captureBaseSnapshotIfNeeded(styleLayerId, styleLayer);
		}
		return owner;
	}

	/**
	 * Returns the captured base style for `styleLayerId`, or undefined if
	 * no snapshot has been taken yet (style layer was never resolved).
	 */
	getBaseSnapshot(styleLayerId: string): ServiceLayer | undefined {
		return this._baseStyleSnapshot.get(styleLayerId);
	}

	/**
	 * Seeds the base-style snapshot with a known-pristine style layer (the
	 * creator's unmodified output). The EsriLayerManager style builder calls
	 * this *before* any build-time rewrite (e.g. the heatmap "bubble" glow) so
	 * the snapshot is the true base — not a later override. No-op once captured,
	 * so the first (pristine) build wins. Without this the snapshot is captured
	 * lazily from the LIVE style and could record an applied glow/colour-by,
	 * breaking `restoreLayerToBaseStyle` (e.g. toggling the heatmap off).
	 */
	seedBaseSnapshot(styleLayerId: string, styleLayer: ServiceLayer): void {
		this._captureBaseSnapshotIfNeeded(styleLayerId, styleLayer);
	}

	private _captureBaseSnapshotIfNeeded(
		styleLayerId: string,
		styleLayer: ServiceLayer,
	): void {
		if (this._baseStyleSnapshot.has(styleLayerId)) return;
		// Deep clone via JSON so subsequent setStyleLayer calls do not mutate
		// the captured snapshot. ServiceLayer is a plain spec object (no
		// functions, no Date instances), so JSON round-trip is safe.
		const snapshot = JSON.parse(JSON.stringify(styleLayer)) as ServiceLayer;
		this._baseStyleSnapshot.set(styleLayerId, snapshot);
	}

	set geoJsonLayerView(geoJsonLayerView: GeoJSONLayerView) {
		this._geoJsonLayerView = geoJsonLayerView;
		if (this._selectedItems.length > 0) {
			this._geoJsonHighlightHandle?.remove();
			this._geoJsonHighlightHandle =
				this._geoJsonLayerView.highlight(this._selectedItems);
		}
	}
	get geoJsonLayerView(): GeoJSONLayerView | undefined {
		return this._geoJsonLayerView;
	}

	get invalid(): boolean {
		return this._invalid;
	}

	get layerInfo(): CompositeLayerProps {
		return this._layerInfo;
	}

	set layerInfo(layerInfo: CompositeLayerProps) {
		// Skip `visible` when diffing: config updates always send visible:true
		// (ConfigAdaptorLayer default), which must never overwrite a user-toggled
		// hidden state. Visibility changes go through toggleLayerVisibility() exclusively.
		const { visible: _newVisible, ...newRest } = layerInfo;
		const { visible: _curVisible, ...curRest } = this._layerInfo;
		const hasChanged = JSON.stringify(newRest) !== JSON.stringify(curRest);
		if (layerInfo.assetFilter === undefined) {
			layerInfo.assetFilter = this._assetFilter;
		}
		// Preserve the current active (visible) state — only toggleLayerVisibility()
		// is allowed to change it. ConfigAdaptorLayer always emits visible:true on
		// updates (SERVICE_RELOADED path), which would silently undo user toggles.
		this._layerInfo = { ...layerInfo, visible: this._layerInfo.visible };
		if (hasChanged) {
			this.delete();
			this._createAllLayers(layerInfo);
			this.fire(LAYER_UPDATED);
			this.draw();
			// Re-apply visibility to the freshly-created sub-layers. The VTL may not
			// have the new style layers yet (loadStyle is triggered separately), so
			// this call may be a no-op — but onFinalStyleLoaded will correct it.
			this.refreshLayerVisibility();
		}
	}

	get id(): CompositeLayerProps['id'] {
		return this._layer.id;
	}

	get idPath(): CompositeLayerProps['idPath'] {
		return this._layerInfo.idPath;
	}

	get selectedId(): CompositeLayerProps['id'] {
		return this._selectedLayer?.id || '';
	}

	get clusterId(): CompositeLayerProps['id'] | undefined {
		// return this._clusterLayer?.id;
		return undefined;
	}

	get displayName(): CompositeLayerProps['displayName'] {
		return this.layerInfo.displayName;
	}

	get icon(): CompositeLayerProps['icon'] {
		return this.layerInfo.icon;
	}

	set loaded(loaded: CompositeLayerProps['loaded']) {
		if (this.layerInfo.loaded !== loaded) {
			this.fire(LAYER_UPDATED);
		}
		this.layerInfo.loaded = loaded;
		this.draw();
	}

	get loaded(): CompositeLayerProps['loaded'] {
		return this.layerInfo.loaded;
	}

	set errored(errored: CompositeLayerProps['errored']) {
		if (this.layerInfo.errored !== errored) {
			this.fire(LAYER_UPDATED);
		}
		this.layerInfo.errored = errored;
	}

	get errored(): CompositeLayerProps['errored'] {
		return this.layerInfo.errored;
	}

	get serviceId(): CompositeLayerProps['serviceId'] {
		return this.layerInfo.serviceId;
	}

	get source(): string {
		if (
			this.layerInfo.source &&
			typeof this.layerInfo.source === 'string'
		) {
			return this.layerInfo.source;
		}
		return this.serviceId;
	}

	get sourceLayer(): CompositeLayerProps['sourceLayer'] {
		return this.layerInfo.sourceLayer;
	}

	get color(): CompositeLayerProps['color'] {
		return this.layerInfo.color;
	}

	get selectedColor(): CompositeLayerProps['selectedColor'] {
		return this.layerInfo.selectedColor;
	}

	get type(): CompositeLayerProps['type'] {
		return this.layerInfo.type;
	}

	get active(): CompositeLayerProps['visible'] {
		return this._layerInfo.visible ?? true;
	}

	set active(active: CompositeLayerProps['visible']) {
		this._layerInfo.visible = active;
	}

	get selectedItems(): string[] {
		return this._selectedItems;
	}

	set selectedItems(items: string[]) {
		this._selectedItems = items;
		this._layer.selectedItems = items;
		if (this._selectedLayer) this._selectedLayer.selectedItems = items;
		this._geoJsonHighlightHandle?.remove();
		if (this._geoJsonLayerView) {
			this._geoJsonHighlightHandle =
				this._geoJsonLayerView.highlight(items);
		}
		// if (this._flowLayer) this._flowLayer.selectedItems = items;
		// if (this._lineSymbolLayer) this._lineSymbolLayer.selectedItems = items;
	}

	get highlightedItems(): string[] {
		return this._highlightItems;
	}

	set highlightedItems(items: string[]) {
		this._highlightItems = items;
		this._highlightBackgroundLayer.highlightedItems = items;
		this._highlightForegroundLayer.highlightedItems = items;
	}

	set iconSet(iconSet: IconSet) {
		this._iconSet = iconSet;
		this._composite.forEach(layer => {
			layer.iconSet = this._iconSet;
		});
	}

	get iconSet(): IconSet {
		return this._iconSet;
	}

	get properties(): CompositeLayerProps['properties'] {
		return this.layerInfo.properties;
	}

	get hasClustering(): CompositeLayerProps['cluster'] {
		return this.layerInfo.cluster;
	}

	set background(background: CompositeLayerProps['background']) {
		this.layerInfo = { ...this.layerInfo, background };
	}

	get showFlow(): CompositeLayerProps['showFlow'] {
		return this.layerInfo.showFlow;
	}

	get flowMinZoom(): CompositeLayerProps['flowMinZoom'] {
		return this.layerInfo.flowMinZoom;
	}

	get onMap(): boolean {
		return this._composite.every(layer => layer.onMap);
	}

	get selectable(): boolean {
		return this.layerInfo.selectable ?? true;
	}

	get minZoom(): CompositeLayerProps['minZoom'] {
		return this.layerInfo.minZoom;
	}

	get maxZoom(): CompositeLayerProps['maxZoom'] {
		return this.layerInfo.maxZoom;
	}

	get styleLayer(): any {
		const styleLayer = this._layer.styleLayer;
		const layerFilter = this._layer.filter;
		if (
			JSON.stringify(styleLayer.filter) !== JSON.stringify(layerFilter) &&
			layerFilter
		) {
			styleLayer.filter = layerFilter;
		}
		return styleLayer;
	}

	/** Polygon outline (line) style layer built alongside the fill, if any. */
	get styleLayerSecond(): any {
		return this._layer.styleLayerSecond;
	}

	get styleLayerSelected(): any {
		return this._selectedLayer?.styleLayerSelected;
	}

	get styleLayerHighlighted(): any {
		const styleLayer = this._highlightBackgroundLayer?.styleLayer;
		const layerFilter = this._highlightBackgroundLayer?.filter;
		if (
			styleLayer &&
			layerFilter &&
			JSON.stringify(styleLayer.filter) !== JSON.stringify(layerFilter)
		) {
			styleLayer.filter = layerFilter;
		}
		return styleLayer;
	}

	clearSelectedItems(): void {
		this.selectedItems = [];
	}

	clearHighlightedItems(): void {
		this.highlightedItems = [];
	}

	toggleLayerVisibility(newState = !this.active): void {
		if (newState === this.active) return;
		this.active = newState;
		this.refreshLayerVisibility();
	}

	setFilter(filter: boolean | string[]): void {
		this._assetFilter = filter;
		this.layerInfo = { ...this.layerInfo, assetFilter: filter };
	}

	draw(): void {
		this._composite?.forEach(layer => {
			layer.draw();
		});
	}

	refreshLayerVisibility(): void {
		if (this.active) {
			this.show();
		} else {
			this.hide();
		}
	}

	hide(): void {
		this._composite.forEach(layer => layer.hide());
		if (this._geoJsonLayerView) {
			this._geoJsonLayerView.layer.visible = false;
		}
	}

	show(): void {
		this._composite.forEach(layer => layer.show());
		if (this._geoJsonLayerView) {
			this._geoJsonLayerView.layer.visible = true;
		}
	}

	delete(): void {
		this._composite.forEach(layer => layer.delete());
	}

	setTimelineFilter(
		currentTime: number,
		animatedProps: AnimationProps,
	): void {
		if (!animatedProps?.inView || !animatedProps?.outView) return;
		const onset = ['<=', ['get', animatedProps.inView], currentTime];
		const offset = ['>=', ['get', animatedProps.outView], currentTime];
		this._filter = ['all', onset, offset];
		this._layer.filter = this._filter;
	}

	private _isValidLayer(layerInfo: CompositeLayerProps): boolean {
		if (layerInfo.tileLayerName && !layerInfo.sourceLayer) return false;
		return true;
	}

	private _createAllLayers(layerInfo: CompositeLayerProps): void {
		this._layer = new MainLayer(layerInfo, this._vectorTileLayer, this);
		this._layer.selectedItems = this.selectedItems;
		this._createSelectedLayer(layerInfo);

		this._highlightBackgroundLayer = new HighlightBackgroundLayer(
			layerInfo,
			undefined,
			undefined,
			this,
		);
		this._highlightBackgroundLayer.highlightedItems = this.highlightedItems;
		this._highlightForegroundLayer = new HighlightForegroundLayer(
			layerInfo,
			undefined,
			this,
		);
		this._highlightForegroundLayer.highlightedItems = this.highlightedItems;

		this._composite = [
			this._layer,
			...(this._selectedLayer ? [this._selectedLayer] : []),
			this._highlightBackgroundLayer,
			this._highlightForegroundLayer,
		] as LayerCreators[];
		logger.debug(
			`layers composite created for ${this.id}`,
			this._composite,
		);
	}

	private _createSelectedLayer(layerInfo: CompositeLayerProps): void {
		if (layerInfo.type === 'symbol') {
			this._selectedLayer = new SelectedLayer(
				layerInfo,
				this._vectorTileLayer,
				this,
			);
			this._selectedLayer.selectedItems = this.selectedItems;
		} else if (['line', 'polygon'].includes(layerInfo.type)) {
			logger.info(
				'esri code ignores layerInfo.pattern for selected line layer',
			);
			this._selectedLayer = new SelectedLayer(
				{
					...layerInfo,
					selected: true,
				},
				this._vectorTileLayer,
				this,
			);
			this._selectedLayer.selectedItems = this.selectedItems;
		} else {
			this._selectedLayer = undefined;
		}
	}

	// private _createClusterLayers(layerInfo: CompositeLayerProps): void {
	// 	if (this.hasClustering) {
	// 		this._clusterLayer = new ClusterCreatorLayer(layerInfo, this._map);
	// 	} else {
	// 		this._clusterLayer = undefined;
	// 	}
	// }

	// private _createLineSymbolLayer(layerInfo: CompositeLayerProps): void {
	// 	if (this.layerInfo.type === 'line' && this.layerInfo.icon) {
	// 		this._lineSymbolLayer = new LineSymboCreatorlLayer(
	// 			layerInfo,
	// 			this._map,
	// 		);
	// 	} else {
	// 		this._lineSymbolLayer = undefined;
	// 	}
	// }

	// private _createFlowLayer(layerInfo: CompositeLayerProps): void {
	// 	if (this.showFlow) {
	// 		this._flowLayer = new FlowCreatorLayer(layerInfo, this._map);
	// 		this._flowLayer.selectedItems = this.selectedItems;
	// 	} else {
	// 		this._flowLayer = undefined;
	// 	}
	// }
}
